import React from 'react';

export interface MissionVisionItem {
  icon: string;
  label: string;
  color: string;
  title: string;
  text: string;
}

export interface ValueItem {
  icon: string;
  title: string;
  desc: string;
  color: string;
}

export interface ExperienceArea {
  area: string;
  skills: string[];
  color: string;
}

export interface MissionVisionData {
  mission: {
    label: string;
    title: string;
    text: string;
  };
  vision: {
    label: string;
    title: string;
    text: string;
  };
  valuesTitle: string;
  valuesSub: string;
  valuesHighlight: string;
  values: ValueItem[];
  experienceTitle: string;
  areas: ExperienceArea[];
}

interface MissionVisionProps {
  data: MissionVisionData;
  isEditing?: boolean;
  onChange?: (data: MissionVisionData) => void;
}

const MissionVision: React.FC<MissionVisionProps> = ({ data, isEditing = false, onChange }) => {
  const handleMissionChange = (field: 'label' | 'title' | 'text', val: string) => {
    if (!onChange) return;
    onChange({
      ...data,
      mission: { ...data.mission, [field]: val },
    });
  };

  const handleVisionChange = (field: 'label' | 'title' | 'text', val: string) => {
    if (!onChange) return;
    onChange({
      ...data,
      vision: { ...data.vision, [field]: val },
    });
  };

  const handleValueChange = (index: number, field: 'title' | 'desc', val: string) => {
    if (!onChange) return;
    const updated = data.values.map((v, i) => (i === index ? { ...v, [field]: val } : v));
    onChange({ ...data, values: updated });
  };

  const handleAreaChange = (index: number, field: 'area' | 'skills', val: any) => {
    if (!onChange) return;
    const updated = data.areas.map((a, i) => (i === index ? { ...a, [field]: val } : a));
    onChange({ ...data, areas: updated });
  };

  return (
    <section className="relative py-24 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(140,82,255,0.12)_0%,transparent_60%)]" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Mission & Vision Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* Misión */}
          <div className="relative bg-white/3 border border-white/8 rounded-3xl p-8 overflow-hidden group hover:border-white/20 transition-all duration-500">
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(140,82,255,0.1) 0%, transparent 60%)' }}
            />
            <div
              className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
              style={{ background: '#8c52ff20', border: '1px solid #8c52ff30' }}
            >
              <i className="ri-focus-3-line text-2xl text-[#8c52ff]" />
            </div>

            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={data.mission.label}
                  onChange={(e) => handleMissionChange('label', e.target.value)}
                  className="bg-black/40 border border-purple-500/40 rounded px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-[#8c52ff] focus:outline-none"
                  placeholder="Etiqueta"
                />
                <input
                  type="text"
                  value={data.mission.title}
                  onChange={(e) => handleMissionChange('title', e.target.value)}
                  className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-white font-black text-xl focus:outline-none"
                  placeholder="Título de la Misión"
                />
                <textarea
                  value={data.mission.text}
                  onChange={(e) => handleMissionChange('text', e.target.value)}
                  rows={4}
                  className="w-full bg-black/40 border border-purple-500/30 rounded-lg p-2.5 text-white/70 text-sm focus:outline-none"
                  placeholder="Texto descriptivo de la misión"
                />
              </div>
            ) : (
              <>
                <span className="text-xs font-bold uppercase tracking-widest mb-2 block text-[#8c52ff]">
                  {data.mission.label}
                </span>
                <h3 className="text-white font-black text-xl md:text-2xl mb-4">{data.mission.title}</h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">{data.mission.text}</p>
              </>
            )}
          </div>

          {/* Visión */}
          <div className="relative bg-white/3 border border-white/8 rounded-3xl p-8 overflow-hidden group hover:border-white/20 transition-all duration-500">
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(0,194,255,0.1) 0%, transparent 60%)' }}
            />
            <div
              className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
              style={{ background: '#00c2ff20', border: '1px solid #00c2ff30' }}
            >
              <i className="ri-eye-line text-2xl text-[#00c2ff]" />
            </div>

            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={data.vision.label}
                  onChange={(e) => handleVisionChange('label', e.target.value)}
                  className="bg-black/40 border border-cyan-500/40 rounded px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-[#00c2ff] focus:outline-none"
                  placeholder="Etiqueta"
                />
                <input
                  type="text"
                  value={data.vision.title}
                  onChange={(e) => handleVisionChange('title', e.target.value)}
                  className="w-full bg-black/40 border border-cyan-500/40 rounded-lg px-3 py-2 text-white font-black text-xl focus:outline-none"
                  placeholder="Título de la Visión"
                />
                <textarea
                  value={data.vision.text}
                  onChange={(e) => handleVisionChange('text', e.target.value)}
                  rows={4}
                  className="w-full bg-black/40 border border-cyan-500/30 rounded-lg p-2.5 text-white/70 text-sm focus:outline-none"
                  placeholder="Texto descriptivo de la visión"
                />
              </div>
            ) : (
              <>
                <span className="text-xs font-bold uppercase tracking-widest mb-2 block text-[#00c2ff]">
                  {data.vision.label}
                </span>
                <h3 className="text-white font-black text-xl md:text-2xl mb-4">{data.vision.title}</h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">{data.vision.text}</p>
              </>
            )}
          </div>
        </div>

        {/* Values */}
        <div className="text-center mb-12">
          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-2 mb-6">
              <input
                type="text"
                value={data.valuesTitle}
                onChange={(e) => onChange && onChange({ ...data, valuesTitle: e.target.value })}
                className="bg-black/40 border border-pink-500/40 rounded px-3 py-1 text-[#FF2DAA] text-xs font-bold uppercase tracking-widest text-center focus:outline-none"
                placeholder="Título de sección valores"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={data.valuesSub}
                  onChange={(e) => onChange && onChange({ ...data, valuesSub: e.target.value })}
                  className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white font-black text-xl text-right focus:outline-none"
                />
                <input
                  type="text"
                  value={data.valuesHighlight}
                  onChange={(e) => onChange && onChange({ ...data, valuesHighlight: e.target.value })}
                  className="flex-1 bg-black/40 border border-purple-500/40 rounded px-3 py-2 text-purple-400 font-black text-xl text-left focus:outline-none"
                />
              </div>
            </div>
          ) : (
            <>
              <p className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-3">{data.valuesTitle}</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">
                {data.valuesSub}{' '}
                <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                  {data.valuesHighlight}
                </span>
              </h2>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.values.map((v, i) => (
            <div
              key={i}
              className="group bg-white/3 border border-white/8 rounded-2xl p-6 text-center hover:border-white/20 transition-all duration-500 hover:-translate-y-1"
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${v.color}20`, border: `1px solid ${v.color}30` }}
              >
                <i className={`${v.icon} text-2xl`} style={{ color: v.color }} />
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={v.title}
                    onChange={(e) => handleValueChange(i, 'title', e.target.value)}
                    className="w-full bg-black/40 border border-purple-500/40 rounded px-2 py-1 text-white font-bold text-center text-sm focus:outline-none"
                  />
                  <textarea
                    value={v.desc}
                    onChange={(e) => handleValueChange(i, 'desc', e.target.value)}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/60 text-xs text-center focus:outline-none focus:border-purple-500"
                  />
                </div>
              ) : (
                <>
                  <h4 className="text-white font-bold text-base mb-2">{v.title}</h4>
                  <p className="text-white/50 text-sm leading-relaxed">{v.desc}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Experience Stats */}
        <div className="mt-20 bg-white/3 border border-white/8 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-10">
            {isEditing ? (
              <input
                type="text"
                value={data.experienceTitle}
                onChange={(e) => onChange && onChange({ ...data, experienceTitle: e.target.value })}
                className="bg-black/40 border border-purple-500/40 rounded-lg px-4 py-2 text-white font-black text-2xl text-center focus:outline-none"
              />
            ) : (
              <h3 className="text-white font-black text-2xl md:text-3xl">{data.experienceTitle}</h3>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {data.areas.map((area, areaIdx) => (
              <div key={areaIdx} className="space-y-3">
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={area.area}
                      onChange={(e) => handleAreaChange(areaIdx, 'area', e.target.value)}
                      className="w-full bg-black/40 border border-purple-500/40 rounded px-3 py-1.5 font-bold text-sm focus:outline-none"
                      style={{ color: area.color }}
                    />
                    <label className="text-[10px] text-white/40 block">Habilidades (separadas por coma):</label>
                    <textarea
                      value={area.skills.join(', ')}
                      onChange={(e) =>
                        handleAreaChange(
                          areaIdx,
                          'skills',
                          e.target.value.split(',').map((s) => s.trim())
                        )
                      }
                      rows={4}
                      className="w-full bg-black/40 border border-white/10 rounded p-2 text-white/70 text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                ) : (
                  <>
                    <h4 className="font-bold text-base mb-4" style={{ color: area.color }}>
                      {area.area}
                    </h4>
                    <ul className="space-y-2">
                      {area.skills.map((skill, sIdx) => (
                        <li key={sIdx} className="flex items-center gap-2 text-white/60 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: area.color }} />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;
